import { useEffect, useState } from 'react';

import DOMPurify from 'dompurify';
import PubSub from 'pubsub-js';

import { logger } from '../../logger';
import { getAppConfig, updateSessionStorage } from '../../utilities';
import { cacheAccountAndRegion, redirectTo } from '../../utilities/common';
import { REGIONAL_ACCOUNTS, SELECTED_ACCOUNT, SELECTED_REGION } from '../constants';

function useAccount(location) {
  const [accountAndRegion, setAccountAndRegion] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [isRegionalAccount, setIsRegionalAccount] = useState(false);

  const makeAccAndRegionCompatibleWithReactProps = accOrRegion =>
    accOrRegion.length
      ? accOrRegion.map(item => ({
          value: item.fullname,
          label: item.fullname,
          selected: item.selected,
          ...item
        }))
      : [];

  const redirectoToCurrentPage = () => {
    const { host, path } = getAppConfig('server');
    if (isRegionalAccount) {
      const URI = encodeURI(`${host}${location}${window.location.search}`);
      redirectTo(URI);
    } else {
      /* When users try to change account while viewing contract or order details
      -- Specific contract or order ID will not be available on the new account selection
      causing a 403 error. Therfore, it is important to redirect users to the dashboard
      in a sceanrio as the above */
      const redirectToDashboard = getAppConfig('tnc').redirectAfterLogin;
      const URI = encodeURI(`${host}${path}${redirectToDashboard}`);
      window.location.href = DOMPurify.sanitize(URI);
    }
  };

  const updateAccountSelection = newAccountSelection => {
    const { accounts = '' } = accountAndRegion;
    const updatedAccount = accounts.find(region => region.fullname === newAccountSelection);
    updateSessionStorage.setItem(SELECTED_ACCOUNT, updatedAccount);
    PubSub.publish('isRegionalAccountConfirmed', REGIONAL_ACCOUNTS.includes(updatedAccount?.id));
    redirectoToCurrentPage();
  };

  const updateRegionSelection = newRegionSelection => {
    const { regions = '' } = accountAndRegion;
    const updatedRegion = regions.find(region => region.fullname === newRegionSelection);
    updateSessionStorage.setItem(SELECTED_REGION, updatedRegion);
    redirectoToCurrentPage();
  };

  const setAccAndReg = async () => {
    try {
      const { accounts = [], regions = [] } = await cacheAccountAndRegion();
      const acc = makeAccAndRegionCompatibleWithReactProps(accounts);
      const reg = makeAccAndRegionCompatibleWithReactProps(regions);
      // if null: lds ring will be active, when truthy like [] or {}, lds ring will be turned off
      const activeAccount = (!!accounts.length && accounts.find(item => item.selected)) || {};
      const activeRegion = (!!regions.length && regions.find(item => item.selected)) || {};
      setAccountAndRegion({ accounts: acc, regions: reg });
      setSelectedAccount(activeAccount);
      setSelectedRegion(activeRegion);
      setIsRegionalAccount(REGIONAL_ACCOUNTS.includes(activeAccount.id));
    } catch (error) {
      logger.error('Error in setting Accounts data', error);
    }
  };

  useEffect(() => {
    setAccAndReg(); // Accounts API call
  }, []);

  return {
    accountAndRegion,
    updateAccountSelection,
    updateRegionSelection,
    selectedAccount,
    selectedRegion,
    isRegionalAccount
  };
}

export default useAccount;
